import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaFacebookF, FaInstagram, FaGlobe, FaCode, FaRocket, FaEnvelope } from "react-icons/fa";
import "./HomePage.css";
import logo from "./home_page.png";

// High-tech cyber stream lines for the "wow" factor
const ENGINE_STREAMS = {
  en: [
    "EMKA_CORE // INITIALIZING OUR ENGINE FOR WHAT COMES NEXT...",
    "IRAQ_NODE // ONLINE — BUILDING FROM IRAQ, CONNECTING TO THE WORLD...",
    "IDEA_ENGINE // PROCESSING AMBITIOUS IDEAS INTO REAL DIGITAL PRODUCTS...",
    "CODEBASE // BUILDING CLEAN, SCALABLE SYSTEMS DESIGNED TO MAKE AN IMPACT...",
    "IMPACT_LAYER // ACTIVATED — TURNING TECHNOLOGY INTO MEANINGFUL RESULTS...",
    "GLOBAL_DEPLOY // READY — WHAT WE BUILD HERE CAN REACH ANYWHERE...",
  ],

  ar: [
    "EMKA_CORE // جاري تهيئة محركنا لما هو قادم...",
    "IRAQ_NODE // متصل — نبني من العراق ونتصل بالعالم...",
    "IDEA_ENGINE // جاري تحويل الأفكار الطموحة إلى منتجات رقمية حقيقية...",
    "CODEBASE // جاري بناء أنظمة مرنة وقابلة للتوسع لصناعة أثر حقيقي...",
    "IMPACT_LAYER // تم تفعيل الأثر — نحوّل التقنية إلى نتائج ملموسة...",
    "GLOBAL_DEPLOY // جاهز — ما نبنيه هنا يمكن أن يصل إلى أي مكان...",
  ],
};

const HomePage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);

  const [streamIndex, setStreamIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setStreamIndex((i) => (i + 1) % ENGINE_STREAMS.en.length);
    }, 3200);

    return () => clearInterval(id);
  }, []);

  // ---- Soft ambient glow that follows the cursor ----
  const heroRef = useRef(null);
  const handleMouseMove = useCallback((e) => {
    const el = heroRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--glow-x", `${e.clientX - rect.left}px`);
    el.style.setProperty("--glow-y", `${e.clientY - rect.top}px`);
  }, []);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    document.dir = lang === "ar" ? "rtl" : "ltr";
    setShowLangMenu(false);
  };

  return (
    <div className="hero hero--v2" ref={heroRef} onMouseMove5={handleMouseMove} onMouseMove={handleMouseMove}>
      <div className="ambient-glow" aria-hidden="true" />

      {/* Language Switcher in the top right corner */}
      <div className="hero-topbar">
        <div className="lang-switcher">
          <button
            type="button"
            className="lang-toggle"
            onClick={() => setShowLangMenu((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={showLangMenu}
          >
            <FaGlobe />
            <span>{i18n.language === "ar" ? "AR" : "EN"}</span>
          </button>
          {showLangMenu && (
            <div className="lang-dropdown" role="listbox">
              <div role="option" onClick={() => changeLanguage("en")}>English</div>
              <div role="option" onClick={() => changeLanguage("ar")}>العربية</div>
            </div>
          )}
        </div>
      </div>

      {/* Main content — centered */}
      <div className="hero-main">
        {/* Prominent, beautifully styled logo */}
        <div className="hero-logo-container reveal" style={{ "--delay": "0s" }}>
          <img src={logo} alt="24Hify Logo" className="hero-brand-logo" />
        </div>

        <span className="eyebrow reveal" style={{ "--delay": "0.1s" }}>
          // EMKA — always compiling
        </span>
        <h1 className="hero-title reveal" style={{ "--delay": "0.2s" }}>
          {t("Software that never clocks out.")}
        </h1>
        <p className="hero-tagline reveal" style={{ "--delay": "0.3s" }}>
          {t("Round-the-clock delivery from a team that ships while your timezone sleeps.")}
        </p>

        {/* Wow Factor: Live Engine Status Bar */}
        <div className="engine-block reveal" style={{ "--delay": "0.4s" }}>
  <div className="engine-monitor">
    <span className="engine-pulse" />
    <span className="engine-text" key={streamIndex}>
      {ENGINE_STREAMS[i18n.language === "ar" ? "ar" : "en"][streamIndex]}
    </span>
  </div>
</div>

        {/* Nav — three quiet, editorial cards */}
        <div className="nav-row">
          <div
            className="nav-card reveal"
            style={{ "--delay": "0.5s" }}
            onClick={() => navigate("/about")}
          >
            <FaCode className="nav-card-icon" />
            <span className="nav-card-label">{t("About Us")}</span>
            <span className="nav-card-sub">{t("Our mission and team")}</span>
            <span className="nav-card-arrow">→</span>
          </div>

          <div
            className="nav-card reveal"
            style={{ "--delay": "0.6s" }}
            onClick={() => navigate("/projects")}
          >
            <FaRocket className="nav-card-icon" />
            <span className="nav-card-label">{t("Projects")}</span>
            <span className="nav-card-sub">{t("See our latest work")}</span>
            <span className="nav-card-arrow">→</span>
          </div>

          <div
            className="nav-card reveal"
            style={{ "--delay": "0.7s" }}
            onClick={() => navigate("/contact")}
          >
            <FaEnvelope className="nav-card-icon" />
            <span className="nav-card-label">{t("Contact")}</span>
            <span className="nav-card-sub">{t("Start your project now")}</span>
            
            <span className="nav-card-arrow">→</span>
          </div>
        </div>

        {/* Social media icons placed at the absolute bottom center */}
       <div className="bottom-social-row reveal">
          <a
            href="https://www.facebook.com/EmkaCode.tech"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <FaFacebookF />
          </a>

          <a
            href="https://www.instagram.com/emkacode/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <FaInstagram />
          </a>
        </div>
      </div>
    </div>
  );
};
export default HomePage;